/**
 * Shared banner utility functions
 * Used across festival, group, store, and topic pages
 */

import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

/**
 * Initialize banner swiper for a given container
 * @param {string} containerId - The ID of the swiper container element
 * @param {Object} options - Optional configuration overrides
 * @param {boolean} options.autoplay - Whether to enable autoplay (default: true)
 */
export function initializeBannerSwiper(containerId, options = {}) {
  let bannerSwiperContainer = document.getElementById(containerId);
  if (!bannerSwiperContainer) return;

  let slides = bannerSwiperContainer.querySelectorAll('.swiper-slide');
  if (slides.length === 0) return;

  // Check if pagination element exists
  let paginationEl = bannerSwiperContainer.querySelector('.swiper-pagination');
  let nextButton = bannerSwiperContainer.querySelector('.swiper-button-next');
  let prevButton = bannerSwiperContainer.querySelector('.swiper-button-prev');

  // Default autoplay to true unless explicitly set to false
  let enableAutoplay = options.autoplay !== false;

  let swiperConfig = {
    modules: [Navigation, Pagination, Autoplay],
    slidesPerView: 1,
    spaceBetween: 10,
    loop: slides.length > 1,
    autoplay: enableAutoplay && slides.length > 1 ? {
      delay: 4000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    } : false,
    breakpoints: {
      768: {
        spaceBetween: 15,
      },
    },
  };

  // Only add pagination if element exists
  if (paginationEl) {
    swiperConfig.pagination = {
      el: paginationEl,
      clickable: true,
    };
  }

  // Only add navigation if buttons exist
  if (nextButton && prevButton) {
    swiperConfig.navigation = {
      nextEl: nextButton,
      prevEl: prevButton,
    };
  }

  try {
    let bannerSwiper = new Swiper(`#${containerId}`, swiperConfig);
    return bannerSwiper;
  } catch (error) {
    console.error(`Error initializing banner swiper for ${containerId}:`, error);
    return null;
  }
}

/**
 * Initialize click handlers for static banners
 * Opens banner links in a new tab when clicked
 */
export function initializeStaticBannerClicks() {
  let bannerElements = document.querySelectorAll(".click_static_banner");
  bannerElements.forEach(element => {
    element.addEventListener("click", function (event) {
      event.preventDefault();      
      let banner_link = this.dataset.href;
      let target_link = this.dataset.hrefAlt;
      let redirectUrl = banner_link || target_link;
      if (redirectUrl) {
        try {
          let url = new URL(redirectUrl, window.location.origin);
          let hostname = url.hostname.toLowerCase();
          let internalHosts = window.appconfig?.banner_internal_hosts || [];
          let isInternal = internalHosts.some(host => hostname.includes(host));
          if (isInternal) {
            window.location.href = redirectUrl;
          } else {
            window.open(redirectUrl, '_blank');
            window.focus();
          }
        } catch (e) {
          window.open(redirectUrl, '_blank');
          window.focus();
        }
      }
    });
  });
}